import ShapeUtils from "./ShapeUtils";
import RenderingTracker from "./RenderingTracker";
import * as THREE from "three";

export default class SceneAbstract {
	protected htmlContainer: HTMLElement;

	protected scene: THREE.Scene;
	protected renderer: THREE.WebGLRenderer;

	protected renderingTracker: RenderingTracker;

	protected camera: THREE.PerspectiveCamera;
	protected cameraStartPosition = new THREE.Vector3(0, 0, 100);

	protected shapesContainer: THREE.Group;

	protected assetsPath = "/wp-content/themes/plaudit-theme-child/assets/";

	protected verticalStripesMaterial: THREE.MeshBasicMaterial;
	protected horizontalStripesMaterial: THREE.MeshBasicMaterial;

	protected updateAndRender: () => void;

	protected getCanvasSize = (): {width: number, height: number} => {
		return {
			width: this.htmlContainer.offsetWidth,
			height: this.htmlContainer.offsetHeight
		};
	};

	constructor(
		htmlContainer: HTMLElement,
		canvasStyleClass: string,
		getCanvasSize?: () => {width: number, height: number}
	) {
		this.htmlContainer = htmlContainer;
		if (getCanvasSize !== undefined) {
			this.getCanvasSize = getCanvasSize;
		}

		// Setup scene, camera, and renderer
		const canvasSize = this.getCanvasSize();
		this.scene = new THREE.Scene();

		this.camera = new THREE.PerspectiveCamera(10, canvasSize.width / canvasSize.height, 0.1, 1000);
		this.camera.position.copy(this.cameraStartPosition);

		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			powerPreference: "default",
			alpha: true // Transparent background
		});
		this.renderer.setSize(canvasSize.width, canvasSize.height);
		this.renderer.domElement.setAttribute("aria-hidden", "true");
		this.renderer.domElement.classList.add(canvasStyleClass);

		htmlContainer.prepend(this.renderer.domElement);

		// Add a light
		const light = new THREE.AmbientLight();
		light.intensity = 0.4;
		this.scene.add(light);

		// Prepare material
		const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();

		this.verticalStripesMaterial = ShapeUtils.createMaterialWithTexture(
			ShapeUtils.svgStringToSrcData('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="2048" height="2048"><defs><style>rect{fill:#ffffff;}</style></defs><rect width="128" height="1024"/><rect x="256" width="128" height="1024"/><rect x="512" width="128" height="1024"/><rect x="768" width="128" height="1024"/></svg>'),
			{x: 4, y: 4},
			maxAnisotropy
		);

		this.horizontalStripesMaterial = ShapeUtils.createMaterialWithTexture(
			ShapeUtils.svgStringToSrcData('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="2048" height="2048"><defs><style>rect {fill:#ffffff;}</style></defs><rect y="896" width="1024" height="128"/><rect y="640" width="1024" height="128"/><rect y="384" width="1024" height="128"/><rect y="128" width="1024" height="128"/></svg>'),
			{x: 2.6, y: 2.6},
			maxAnisotropy
		);

		// Create shapes container
		this.shapesContainer = new THREE.Group();
		this.scene.add(this.shapesContainer);

		// Watch for window resizing, visibilitychange and touchend
		this.renderingTracker = new RenderingTracker(
			this.renderer, 
			() => {
				this.updateAndRender();
			},
			window
		);
		
		// Animate in
		requestAnimationFrame(() => {
			this.renderer.domElement.classList.add(canvasStyleClass + "--inited");
		});
	}

	public isRendering(): boolean {
		return this.renderingTracker.isRendering()
	}

	public startRendering() {
		this.renderingTracker.startRendering();
	}
	public pauseRendering() {
		this.renderingTracker.pauseRendering();
	}

}